import { Check, HelpCircle, Minus } from 'lucide-react';
import { type ReactNode } from 'react';
import { Checkbox as RACCheckbox, type CheckboxProps as RACCheckboxProps, composeRenderProps } from 'react-aria-components';
import { cn, focusRing } from '~/shared/components/utils';
import { tv, type VariantProps } from 'tailwind-variants';
import { IconButtonPopover } from '~/shared/IconButtonPopover';

const checkboxStyles = tv({
	slots: {
		base: 'group flex items-start gap-2 p-2 transition',
		box: 'mt-1 flex size-5 flex-shrink-0 items-center justify-center rounded border-2 transition',
		icon: 'text-white group-disabled:text-gray-400 forced-colors:text-[HighlightText]',
		label: 'text-sm text-gray-800 group-disabled:text-gray-400',
		description: 'text-xs text-gray-500 group-disabled:text-gray-400'
	},
	variants: {
		size: {
			small: {
				base: 'text-sm',
				box: 'size-4',
				icon: 'size-3'
			},
			medium: {
				base: 'text-base',
				box: 'size-5',
				icon: 'size-4'
			}
		},
		labelPrefix: {
			true: {
				base: 'group flex items-start'
			}
		},
		isSelected: {
			true: {
				box: 'border-[--color] bg-[--color] [--color:theme(colors.primary.500)] group-pressed:[--color:theme(colors.primary.500/90)]'
			}
		},
		isInvalid: {
			true: {
				box: '[--color:theme(colors.red.600)] group-pressed:[--color:theme(colors.red.700)]'
			}
		},
		isIndeterminate: {
			true: {
				box: 'border-[--color] bg-[--color] [--color:theme(colors.primary.500)] group-pressed:[--color:theme(colors.primary.500/90)]'
			}
		},
		isDisabled: {
			true: {
				base: 'forced-colors:text-gray-200',
				box: '[--color:theme(colors.gray.200)]'
			},
			false: {
				base: 'text-gray-800'
			}
		},
		isSuggested: {
			true: {
				base: 'bg-primary-500/5'
			}
		},
		isAddendumOriginal: {
			true: {
				base: 'bg-blue-500/10'
			}
		},
		isAddendumUpdated: {
			true: {
				base: 'bg-yellow-500/10'
			}
		}
	},

	defaultVariants: {
		size: 'medium'
	}
});

type CheckboxStyleVariants = Omit<VariantProps<typeof checkboxStyles>, 'labelPrefix'>;

interface CheckboxProps extends Omit<RACCheckboxProps, 'children'>, CheckboxStyleVariants {
	label: string;
	labelPrefix?: string;
	description?: string;
	hideCheckboxLabel?: boolean;
	suggestionText?: string;
	isAddendumOriginal?: boolean;
	children?: ReactNode;
}

export function Checkbox({ label, size, labelPrefix, description, suggestionText, isAddendumOriginal, ...props }: CheckboxProps) {
	const {
		base,
		box,
		icon,
		label: labelStyles,
		description: descriptionStyles
	} = checkboxStyles({
		size,
		labelPrefix: !!labelPrefix,
		isInvalid: props.isInvalid,
		isDisabled: props.isDisabled,
		isSelected: props.isSelected,
		isSuggested: props.isSuggested,
		isAddendumOriginal: isAddendumOriginal
	});
	return (
		<RACCheckbox
			{...props}
			aria-label={label}
			className={composeRenderProps(props.className, (className, renderProps) => cn(base(renderProps), className))}
		>
			{renderProps => (
				<>
					<div className={cn(box(renderProps), focusRing(renderProps))}>
						{renderProps.isIndeterminate ? (
							<Minus aria-hidden className={icon()} />
						) : renderProps.isSelected ? (
							<Check aria-hidden className={icon()} />
						) : null}
					</div>
					<div className="flex min-h-[30px] items-center">
						{!props.hideCheckboxLabel && (
							<div className="flex flex-col gap-0.5">
								{labelPrefix ? (
									<span className="flex gap-1">
										<span className="shrink-0">{labelPrefix}</span>
										<span dangerouslySetInnerHTML={{ __html: label }} />
									</span>
								) : (
									<span className={labelStyles()} dangerouslySetInnerHTML={{ __html: label }} />
								)}
								{description && <div className={descriptionStyles()}>{description}</div>}
							</div>
						)}
					</div>

					{suggestionText && (
						<IconButtonPopover
							Icon={HelpCircle}
							aria-label="Show suggested answer reason"
							popoverText={suggestionText}
							size="sm"
							appearance="ghost"
						/>
					)}
					{isAddendumOriginal && (
						<IconButtonPopover
							Icon={HelpCircle}
							aria-label="Marked as original answer"
							popoverText="Answer was marked by the original author of the OASIS"
							size="sm"
							appearance="ghost"
						/>
					)}
				</>
			)}
		</RACCheckbox>
	);
}
